<template>
  <v-container class="edit-team-page py-6 py-md-10">
    <v-btn text color="primary" class="back-button mb-4 px-2" to="/teams">
      <v-icon left>mdi-chevron-left</v-icon>
      Înapoi la cluburi
    </v-btn>

    <div v-if="loading" class="py-12 text-center">
      <v-progress-circular indeterminate color="primary" size="42"></v-progress-circular>
      <div class="text-body-1 mt-4">Se încarcă detaliile clubului…</div>
    </div>

    <template v-else>
      <v-card class="page-header mb-6" elevation="2">
        <v-card-text class="d-flex align-center pa-5 pa-md-6">
          <v-avatar color="blue-grey darken-3" size="52" class="mr-4" tile>
            <v-img v-if="teamLogoUrl" :src="teamLogoUrl" :alt="editingTeam.name"></v-img>
            <span v-else class="white--text text-h6 font-weight-bold">{{ teamInitials }}</span>
          </v-avatar>
          <div class="min-width-0">
            <div class="text-overline primary--text">Administrare club</div>
            <h1 class="text-h4 font-weight-bold text-truncate">{{ editingTeam.name }}</h1>
            <div v-if="editingTeam.simulator" class="secondary-text text-body-2 mt-1">
              <v-icon small class="mr-1">mdi-steering</v-icon>{{ editingTeam.simulator.title }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-chip label small :color="isTeamOwner ? 'amber darken-3' : 'blue darken-1'" dark class="ml-3">
            {{ isTeamOwner ? 'Proprietar' : 'Manager' }}
          </v-chip>
        </v-card-text>
      </v-card>

      <v-form @submit.prevent="saveTeam">
        <v-card class="form-section mb-5" outlined>
          <div class="section-heading d-flex align-center pa-5">
            <v-icon color="primary" class="mr-3">mdi-card-text-outline</v-icon>
            <div>
              <h2 class="text-h6 font-weight-bold">Detalii generale</h2>
              <div class="secondary-text text-body-2">Informațiile de bază ale clubului.</div>
            </div>
          </div>
          <v-divider></v-divider>
          <v-card-text class="pa-5 pa-md-6">
            <v-row v-if="isTeamOwner" align="center" class="mb-4">
              <v-col cols="12" sm="3" class="text-center">
                <v-avatar tile size="112" color="blue-grey darken-3">
                  <v-img v-if="teamLogoUrl" :src="teamLogoUrl" :alt="editingTeam.name"></v-img>
                  <span v-else class="white--text text-h4 font-weight-bold">{{ teamInitials }}</span>
                </v-avatar>
              </v-col>
              <v-col cols="12" sm="9">
                <v-file-input
                  v-model="logoFile"
                  accept="image/jpeg,image/png,image/webp"
                  label="Logo-ul clubului"
                  outlined
                  prepend-icon="mdi-image-outline"
                  hint="JPEG, PNG sau WebP, maximum 1 MB și 1024 × 1024 px."
                  persistent-hint
                  :rules="logoRules"
                  :error-messages="logoDimensionError"
                  show-size
                  @change="setLogoPreview"
                ></v-file-input>
                <v-btn
                  v-if="logoFile || editingTeam.logo"
                  text
                  color="error"
                  class="mt-1"
                  :disabled="saving"
                  @click="requestLogoDeletion"
                >
                  <v-icon left>mdi-delete-outline</v-icon>{{ logoFile ? 'Renunță la imaginea selectată' : 'Șterge logo-ul' }}
                </v-btn>
              </v-col>
            </v-row>
            <v-text-field
              v-model="editingTeam.name"
              label="Numele clubului"
              outlined
              required
              :disabled="!isTeamOwner"
              hint="Doar proprietarul poate schimba numele clubului."
              :persistent-hint="!isTeamOwner"
            ></v-text-field>
          </v-card-text>
        </v-card>

        <v-card class="form-section mb-5" outlined>
          <div class="section-heading d-flex align-center pa-5">
            <v-icon color="primary" class="mr-3">mdi-account-multiple-outline</v-icon>
            <div>
              <h2 class="text-h6 font-weight-bold">Piloți</h2>
              <div class="secondary-text text-body-2">Gestionează membrii activi și invitațiile în așteptare.</div>
            </div>
          </div>
          <v-divider></v-divider>
          <v-card-text class="pa-5 pa-md-6">
            <v-row>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="editingTeam.drivers"
                  :items="teamDrivers"
                  label="Piloți activi"
                  outlined
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  prepend-inner-icon="mdi-racing-helmet"
                  hint="Piloții care fac deja parte din club."
                  persistent-hint
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="editingTeam.pendingDrivers"
                  :items="availableDrivers"
                  label="Invită piloți"
                  outlined
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  prepend-inner-icon="mdi-account-plus-outline"
                  hint="Selectează piloții cărora vrei să le trimiți o invitație."
                  persistent-hint
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="form-section mb-6" outlined>
          <div class="section-heading d-flex align-center pa-5">
            <v-icon color="primary" class="mr-3">mdi-shield-account-outline</v-icon>
            <div>
              <h2 class="text-h6 font-weight-bold">Manageri</h2>
              <div class="secondary-text text-body-2">Persoanele care pot administra acest club.</div>
            </div>
          </div>
          <v-divider></v-divider>
          <v-card-text class="pa-5 pa-md-6">
            <v-autocomplete
              v-model="editingTeam.managers"
              :items="drivers"
              label="Manageri"
              outlined
              multiple
              chips
              small-chips
              deletable-chips
              prepend-inner-icon="mdi-account-cog-outline"
              :disabled="!isTeamOwner"
              hint="Doar proprietarul poate adăuga sau elimina manageri."
              persistent-hint
            ></v-autocomplete>
          </v-card-text>
        </v-card>

        <div class="form-actions d-flex flex-column-reverse flex-sm-row justify-end">
          <v-btn text class="mt-2 mt-sm-0 mr-sm-3" :disabled="saving" @click="discard">Renunță</v-btn>
          <v-btn color="primary" depressed type="submit" :loading="saving" :disabled="!editingTeam.name || !logoIsValid">
            <v-icon left>mdi-content-save-outline</v-icon>Salvează modificările
          </v-btn>
        </div>
      </v-form>

      <v-dialog v-model="deleteLogoDialog" persistent max-width="420">
        <v-card>
          <v-card-title class="headline">Ștergi logo-ul?</v-card-title>
          <v-card-text>Logo-ul actual al clubului va fi șters definitiv.</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text :disabled="deletingLogo" @click="deleteLogoDialog = false">Renunță</v-btn>
            <v-btn color="error" text :loading="deletingLogo" @click="confirmLogoDeletion">Șterge</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  data: () => ({
    availableDrivers: [],
    teamDrivers: [],
    loading: true,
    saving: false,
    deletingLogo: false,
    deleteLogoDialog: false,
    logoFile: null,
    logoPreviewUrl: '',
    logoDimensionError: '',
    logoDimensionsChecking: false,
    logoRules: [
      file => !file || ['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || 'Folosește o imagine JPEG, PNG sau WebP.',
      file => !file || file.size <= 1024 * 1024 || 'Logo-ul trebuie să fie mai mic de 1 MB.'
    ]
  }),
  computed: {
    ...mapState('drivers', ['drivers']),
    ...mapState('user', ['users']),
    ...mapState('team', ['teams', 'editingTeam']),
    ...mapGetters('auth', ['userinfo']),
    isTeamOwner() {
      return Boolean(this.userinfo && this.editingTeam && this.editingTeam.owner && this.userinfo.id === this.editingTeam.owner._id)
    },
    teamInitials() {
      return (this.editingTeam.name || '?').split(/\s+/).slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase()
    },
    logoIsValid() {
      return !this.logoFile || (
        ['image/jpeg', 'image/png', 'image/webp'].includes(this.logoFile.type) &&
        this.logoFile.size <= 1024 * 1024 &&
        !this.logoDimensionsChecking &&
        !this.logoDimensionError
      )
    },
    teamLogoUrl() {
      if (this.logoPreviewUrl) return this.logoPreviewUrl
      if (this.editingTeam.logo && this.editingTeam.logo.filename) {
        return `${process.env.VUE_APP_API_SCHEMA}${process.env.VUE_APP_API_HOST}/api/files/${this.editingTeam.logo.filename}`
      }
      return this.editingTeam.image || ''
    }
  },
  async created() {
    try {
      await Promise.all([this.getDrivers(), this.getUsers(), this.getTeams()])
      await this.getEditingTeam(this.$route.params.id)

      this.teamDrivers = this.editingTeam.drivers.slice()
      this.setAvailableDrivers()

      const isManager = this.editingTeam.managers.some(manager => manager._id === this.userinfo.id)
      if (!this.isTeamOwner && !isManager) {
        this.$router.push({ path: '/404' })
      }
    } finally {
      this.loading = false
    }
  },
  methods: {
    ...mapActions('drivers', ['getDrivers']),
    ...mapActions('user', ['getUsers']),
    ...mapActions('app', ['showSnackbar']),
    ...mapActions('team', ['editTeam', 'uploadTeamLogo', 'deleteTeamLogo', 'getTeams', 'getEditingTeam']),
    discard() {
      this.$router.back()
    },
    async saveTeam() {
      if (!this.logoIsValid) return
      this.saving = true
      try {
        await this.editTeam()
        if (this.logoFile) {
          await this.uploadTeamLogo({teamId: this.$route.params.id, file: this.logoFile})
        }
        this.showSnackbar({
          timeout: 5000,
          value: true,
          color: 'success',
          content: 'Clubul a fost actualizat cu succes'
        })
        this.$router.push({ path: '/teams' })
      } catch (err) {
        this.showSnackbar({
          timeout: 7000,
          value: true,
          color: 'error',
          content: 'Clubul nu a putut fi actualizat. Încearcă din nou.'
        })
      } finally {
        this.saving = false
      }
    },
    setLogoPreview(file) {
      if (this.logoPreviewUrl) URL.revokeObjectURL(this.logoPreviewUrl)
      this.logoDimensionError = ''
      this.logoDimensionsChecking = Boolean(file)
      this.logoPreviewUrl = file ? URL.createObjectURL(file) : ''
      if (file) {
        const image = new Image()
        image.onload = () => {
          this.logoDimensionsChecking = false
          if (image.naturalWidth > 1024 || image.naturalHeight > 1024) {
            this.logoDimensionError = 'Logo-ul nu poate depăși 1024 × 1024 px.'
          }
        }
        image.onerror = () => {
          this.logoDimensionsChecking = false
          this.logoDimensionError = 'Imaginea selectată nu poate fi citită.'
        }
        image.src = this.logoPreviewUrl
      }
    },
    requestLogoDeletion() {
      if (this.logoFile) {
        this.logoFile = null
        this.setLogoPreview(null)
        return
      }
      this.deleteLogoDialog = true
    },
    async confirmLogoDeletion() {
      this.deletingLogo = true
      try {
        await this.deleteTeamLogo(this.$route.params.id)
        this.editingTeam.logo = null
        this.deleteLogoDialog = false
        this.showSnackbar({
          timeout: 5000,
          value: true,
          color: 'success',
          content: 'Logo-ul clubului a fost șters'
        })
      } catch (err) {
        this.showSnackbar({
          timeout: 7000,
          value: true,
          color: 'error',
          content: 'Logo-ul nu a putut fi șters. Încearcă din nou.'
        })
      } finally {
        this.deletingLogo = false
      }
    },
    setAvailableDrivers() {
      const currentDriverIds = this.editingTeam.drivers.map(driver => driver._id)
      this.availableDrivers = this.drivers.filter(driver => !currentDriverIds.includes(driver._id))
    }
  },
  beforeDestroy() {
    if (this.logoPreviewUrl) URL.revokeObjectURL(this.logoPreviewUrl)
  }
}
</script>

<style scoped>
.edit-team-page { max-width: 960px; }
.back-button { margin-left: -8px; }
.page-header, .form-section { border-radius: 4px; }
.secondary-text { color: #7b8490; }
.min-width-0 { min-width: 0; }
.section-heading { min-height: 84px; }
.form-actions { padding-bottom: 8px; }

@media (max-width: 599px) {
  .edit-team-page { padding-right: 12px; padding-left: 12px; }
  .page-header .v-chip { display: none; }
  .form-actions .v-btn { width: 100%; }
}
</style>
